import * as React from "react";
import { Table, Row, Icon, Tooltip } from "antd";
import { Tour } from "../../../lib/model/Tour";
import { Stop } from "../../../lib/model/Stop";
import { cleanTime } from "../../../lib/formatters";
import styled from "styled-components";
import { StopType } from "../../bookMulti/model/BookMultiOrder";
import { ETA } from "../../../lib/model/ETA";
import * as _ from "lodash";
import { WaitingStopTiming } from "../../../lib/model/WaitingStopTiming";
import { TourType } from "../../../lib/model/TourType";

const HeadlineLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.21;
  color: #061a32;
  display: inline-block;
`;

const DataLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  color: #061a32;
  display: inline-block;
`;

const MediumDataLabel = styled.span`
  font-size: 14px;
  font-weight: 300;
  color: #061a32;
  display: inline-block;
`;

const StyledTable = styled(Table)`
  background-color: #ffffff !important;
  .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td {
    background-color: #ffffff !important;
  }
  td {
    padding: 8px 8px !important;
  }
  th {
    padding: 8px 8px !important;
  }
`;

const estimatedInfo = (
  <Tooltip title="Geschätzt. Zeit kann sich im Verlauf des Auftrags ändern.">
    <Icon type="clock-circle" style={{ color: "#386EE8" }} />{" "}
  </Tooltip>
);

const actualInfo = (
  <Tooltip title="Tatsächlich. Zeitangabe des Fahrers.">
    <Icon type="info-circle" style={{ color: "#386EE8" }} />
  </Tooltip>
);

const renderLoads = (
  stop: Stop,
  shouldRenderList: boolean
): React.ReactNode => (
  <>
    <HeadlineLabel>
      {stop.type === StopType.Pickup ? "Beladung" : "Entladung"}
    </HeadlineLabel>
    {shouldRenderList &&
      stop.loads.map(l => (
        <>
          <br />
          <DataLabel>
            {l.count} x {l.label}
            <br />
            {l.length} x {l.width} x {l.height}cm, {l.totalWeight}kg
            <br />
            {l.internalReference}
          </DataLabel>
        </>
      ))}
    <br />
  </>
);

const tableColumns = [
  {
    title: <DataLabel>Firmenname</DataLabel>,
    dataIndex: "companyName",
    key: "companyName",
    width: "25%",
    render: (text: any) => (
      <Row>
        <HeadlineLabel>{text.headline}</HeadlineLabel>
        <br />
        <DataLabel>{text.subline}</DataLabel>
        <br />
      </Row>
    )
  },
  {
    title: <DataLabel>Fracht</DataLabel>,
    dataIndex: "load",
    key: "load",
    width: "25%",
    render: (data: any) => renderLoads(data.stop, data.shouldRenderLoadList)
  },
  {
    title: <DataLabel>Zeitfenster</DataLabel>,
    dataIndex: "timewindow",
    key: "timewindow",
    width: "15%",
    render: (text: any) => <DataLabel>{text}</DataLabel>
  },
  {
    title: <DataLabel>Ankunft</DataLabel>,
    dataIndex: "eta",
    key: "eta",
    width: "15%",
    render: (text: any) => <MediumDataLabel>{text}</MediumDataLabel>
  },
  {
    title: <DataLabel>Abfahrt</DataLabel>,
    dataIndex: "etd",
    key: "etd",
    width: "10%",
    render: (text: any) => <MediumDataLabel>{text}</MediumDataLabel>
  },
  {
    title: <DataLabel>Wartezeit</DataLabel>,
    dataIndex: "waitingtime",
    key: "waitingtime",
    width: "10%",
    render: (text: any) => <MediumDataLabel>{text}</MediumDataLabel>
  }
];

const createTableDataFromStop = (
  stop: Stop,
  shouldRenderLoadList: boolean,
  ETA?: ETA,
  stopTiming?: WaitingStopTiming
): any => ({
  key: stop.address.companyName + stop.loads[0].label,
  companyName: {
    headline: stop.address.companyName,
    subline: (
      <span>
        {stop.address.street} {stop.address.houseNumber}
        <br />
        {stop.address.zipCode} {stop.address.city}
        <br />
        {stop.comment}
        <br />
        {stop.documents &&
          stop.documents.value.map((documentURL, index) => (
            <div key={`dokument${index}`}>
              <a href={documentURL}>
                <Icon type="file" />
                {` Dokument ${index + 1}`}
              </a>
            </div>
          ))}
      </span>
    )
  },
  load: { stop, shouldRenderLoadList },
  timewindow: `${cleanTime(stop.timeFrom)} bis ${cleanTime(stop.timeTo)} Uhr`,
  eta: stopTiming ? (
    `${cleanTime(stopTiming.start)}`
  ) : ETA && ETA.arrive ? (
    ETA.arrive.actual ? (
      <span>
        {`${cleanTime(ETA.arrive.actual)} `}
        {actualInfo}
      </span>
    ) : (
      <span>
        {`${cleanTime(ETA.arrive.estimated!)} `}
        {estimatedInfo}
      </span>
    )
  ) : (
    "–"
  ),
  etd: stopTiming ? (
    `${cleanTime(stopTiming.end)}`
  ) : ETA && ETA.depart ? (
    ETA.depart.actual ? (
      <span>
        {`${cleanTime(ETA.depart.actual)} `}
        {actualInfo}
      </span>
    ) : (
      <span>
        {`${cleanTime(ETA.depart.estimated!)} `}
        {estimatedInfo}
      </span>
    )
  ) : (
    "–"
  ),
  waitingtime: stopTiming ? `${stopTiming.durationMins} min` : "-"
});

type Props = {
  tour: Tour;
  ETAs: ETA[];
};

export class TourDetailsTable extends React.Component<Props> {
  render() {
    const { tour, ETAs } = this.props;
    return (
      <StyledTable
        dataSource={tour.stops.map((s, i) => {
          return createTableDataFromStop(
            s,
            shouldDisplayLoadList(s.type, calculateTourType(tour)),
            _.find(ETAs, e => e.stopIndex === i),
            _.find(
              tour.waitingTimeExtra
                ? tour.waitingTimeExtra.stopIndexTimings
                : [],
              s => s.stopIndex === i
            )
          );
        })}
        columns={tableColumns}
        pagination={false}
      />
    );
  }
}

const shouldDisplayLoadList = (
  stopType: StopType,
  tourType: TourType
): boolean => {
  if (tourType === TourType.Direct && stopType === StopType.Pickup) return true;
  if (tourType === TourType.Distribution && stopType === StopType.Dropoff)
    return true;
  if (tourType === TourType.Collection && stopType === StopType.Pickup)
    return true;
  return false;
};

const calculateTourType = (tour: Tour): TourType => {
  if (tour.stops.length <= 2) return TourType.Direct;
  if (tour.stops.filter(s => s.type === StopType.Dropoff).length > 1)
    return TourType.Distribution;
  return TourType.Collection;
};
